/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Box } from "@mui/material";
import CustomModal from "../../../../components/Modals/CustomModal";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { Button } from "../../../../components/Buttons";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import CustomDatePicker from "../../../../components/DatePicker/CustomDatePicker";
import TimeScheduler from "../../../../components/TimeScheduler/TimeScheduler";
import {
  GREY,
  BRAND,
  BASE,
  PRIMARY,
  BORDER,
  SUCCESS,
  WARNING,
  HELP,
} from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import {
  CAMPAIGN_CREATE_DAYS_LIST,
  initialTimings,
  EMITTER_SUCCESS,
  EMITTER_ERROR,
  TIMEZONE_LIST,
} from "../../../../utils/constants";
import { campaignEndpoints } from "../../../../utils/constants/httpConstants";
import { postRequest } from "../../../../Apis";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  createTimingsForPayload,
  offsetForTimezoneOnDate,
} from "../../../../utils/helpers";
import { useNavigate } from "react-router-dom";
import IconComponent from "../../../../components/Icon/IconComponent";

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Returns today's calendar date (Y/M/D) as it currently is in the given timezone,
// as a dayjs instance anchored to local midnight. This lets us pass a correct
// minDate to the calendar regardless of the user's machine timezone.
const todayInTimezone = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const y = parseInt(get("year"), 10);
    const m = parseInt(get("month"), 10);
    const d = parseInt(get("day"), 10);
    return dayjs(new Date(y, m - 1, d));
  } catch {
    return dayjs().startOf("day");
  }
};

// Convert 12h to minutes from midnight for timeline positioning
const toMinutes = (h, m, type) => {
  let hour = h;
  if (type === "PM" && hour !== 12) hour += 12;
  if (type === "AM" && hour === 12) hour = 0;
  return hour * 60 + m;
};

const ScheduleAutomatedCampaignModal = ({
  open,
  onClose,
  campaignId,
  campaignName,
  onRefresh,
}) => {
  const navigate = useNavigate();

  const currentTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzMatch = TIMEZONE_LIST?.filter((item) => item?.id === currentTz);

  const [timezone, setTimeZone] = useState([
    tzMatch?.[0]?.id || "Asia/Calcutta",
  ]);
  const [scheduleDate, setScheduleDate] = useState(new Date().toString());
  const [getTimings, setGetTimings] = useState([...initialTimings]);
  const [loading, setLoading] = useState(false);
  const [timezoneList, setTimezoneList] = useState([...TIMEZONE_LIST]);
  const [liveTime, setLiveTime] = useState("");

  const selectedTz = Array.isArray(timezone) ? timezone[0] : timezone;

  // Today in the currently selected timezone - used as the calendar's minDate
  // so users in earlier timezones (e.g. PST when local is IST) can still pick today.
  const tzMinDate = useMemo(
    () => todayInTimezone(selectedTz),
    [selectedTz],
  );

  // If the selected schedule date is now before the new timezone's "today",
  // snap it forward so the date picker stays in a valid state.
  useEffect(() => {
    if (!scheduleDate) return;
    const current = dayjs(new Date(scheduleDate)).startOf("day");
    if (current.isBefore(tzMinDate)) {
      setScheduleDate(tzMinDate.toDate().toString());
    }
  }, [tzMinDate]);

  useEffect(() => {
    if (!selectedTz) return;
    const tick = () => {
      try {
        setLiveTime(
          new Date().toLocaleString("en-US", {
            timeZone: selectedTz,
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            weekday: "short",
            month: "short",
            day: "numeric",
          }),
        );
      } catch {
        setLiveTime("");
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [selectedTz]);

  const searchTimezones = (event) => {
    const q = event?.target?.value?.toLowerCase();
    if (q) {
      setTimezoneList(
        TIMEZONE_LIST.filter((tz) =>
          tz?.label?.toLowerCase()?.includes(q),
        ).slice(0, 100),
      );
    } else {
      setTimezoneList([...TIMEZONE_LIST]);
    }
  };

  const allSelected = getTimings.every((t) => t.checked);
  const handleSelectAll = (e) => {
    e?.stopPropagation?.();
    setGetTimings(getTimings.map((t) => ({ ...t, checked: !allSelected })));
  };
  const handleReset = () => setGetTimings([...initialTimings]);

  const handleSchedule = async () => {
    try {
      setLoading(true);
      const timingsPayload = createTimingsForPayload(getTimings);
      const d = new Date(scheduleDate);
      // Offset for the *selected* tz on the *scheduled* date - handles DST
      // (e.g. LA in May → -420, in Jan → -480). Was previously the browser's
      // local offset, which broke any cross-tz scheduling.
      const tzOffset = offsetForTimezoneOnDate(selectedTz, d);
      const resp = await postRequest(
        navigate,
        campaignEndpoints.scheduleAutomatedCampaign,
        {
          campaign_id: campaignId,
          scheduled_date: `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`,
          timezone: selectedTz,
          timings: timingsPayload,
          offset: tzOffset,
          schedule_now: true,
        },
      );
      EventEmitter.emit(
        EMITTER_SUCCESS,
        resp?.data?.message || "Campaign scheduled successfully!",
      );
      localStorage.setItem(
        `reachiq_campaign_ready_shown_${campaignId}`,
        "true",
      );
      onClose();
      onRefresh?.();
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to schedule campaign",
      );
    } finally {
      setLoading(false);
    }
  };

  // --- Computed preview data ---
  const activeDays = getTimings.filter((t) => t.checked);
  const activeDaysCount = activeDays.length;

  const getTotalWeeklyMinutes = () => {
    let total = 0;
    activeDays.forEach((t) => {
      const s = toMinutes(t.startTime, t.startMinute, t.startType);
      const e = toMinutes(t.endTime, t.endMinute, t.endType);
      if (e > s) total += e - s;
    });
    return total;
  };
  const weeklyMins = getTotalWeeklyMinutes();
  const weeklyHours = Math.floor(weeklyMins / 60);
  const weeklyRemMins = weeklyMins % 60;
  const weeklyLabel =
    weeklyMins === 0
      ? "0h"
      : weeklyRemMins === 0
        ? `${weeklyHours}h`
        : `${weeklyHours}h ${weeklyRemMins}m`;

  const formatTimeRange = (t) =>
    `${t.startTime}:${String(t.startMinute).padStart(2, "0")} ${t.startType} to ${t.endTime}:${String(t.endMinute).padStart(2, "0")} ${t.endType}`;

  // Timeline bar calculations (6AM-10PM visible range = 16 hours)
  const TIMELINE_START = 6 * 60; // 6:00 AM
  const TIMELINE_END = 22 * 60; // 10:00 PM
  const TIMELINE_RANGE = TIMELINE_END - TIMELINE_START;

  const getBarStyle = (timing) => {
    const s = toMinutes(timing.startTime, timing.startMinute, timing.startType);
    const e = toMinutes(timing.endTime, timing.endMinute, timing.endType);
    const left = Math.max(0, ((s - TIMELINE_START) / TIMELINE_RANGE) * 100);
    const right = Math.min(100, ((e - TIMELINE_START) / TIMELINE_RANGE) * 100);
    const width = Math.max(0, right - left);
    return { left: `${left}%`, width: `${width}%` };
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      width="50vw"
      remove_padding={true}
      header={{
        heading: { id: "schedule-campaign", label: "Schedule Campaign" },
        description: { id: "campaign-name", label: campaignName },
      }}
      footer={{
        right: (
          <Box sx={s.footerRight}>
            <Button
              label="Close"
              variant="ghost"
              size="sm36"
              onClick={onClose}
            />
            <Button
              label="Schedule Campaign"
              variant="primary"
              size="md40"
              onClick={handleSchedule}
              isLoading={loading}
            />
          </Box>
        ),
      }}
    >
      <Box sx={s.content}>
        {/* ========== LEFT: CONFIGURATION ========== */}
        <Box sx={s.left}>
          {/* Timezone + Date */}
          <Box sx={s.topRow}>
            <Box sx={s.field}>
              <Box sx={s.fieldHead}>
                <TypographyInline
                  size="xs"
                  color={GREY.QUATINARY}
                  fontWeight={fontWeight.MEDIUM}
                  label="Timezone"
                />
                {liveTime && (
                  <Box sx={s.clockBadge}>
                    <Box sx={s.clockDot} />
                    <TypographyInline
                      size="xs"
                      color={GREY.SECONDARY}
                      fontWeight={fontWeight.MEDIUM}
                      label={liveTime}
                    />
                  </Box>
                )}
              </Box>
              <AutoCompleteCustom
                items={timezoneList}
                valueState={timezone}
                idState={timezone}
                setIdState={setTimeZone}
                size="sm36"
                isSearchEnable={true}
                handleSearch={searchTimezones}
                papermenuclass={{ zIndex: 9999 }}
              />
            </Box>
            <Box sx={s.fieldSm}>
              <Box sx={s.fieldHead}>
                <TypographyInline
                  size="xs"
                  color={GREY.QUATINARY}
                  fontWeight={fontWeight.MEDIUM}
                  label="Start date"
                />
              </Box>
              <CustomDatePicker
                minDate={tzMinDate}
                value={scheduleDate}
                onChange={(v) => setScheduleDate(v?.$d?.toString())}
                size="sm36"
              />
            </Box>
          </Box>

          {/* Divider */}
          <Box sx={s.divider} />

          {/* Sending Window Header */}
          <Box sx={s.sectionHead}>
            <Box sx={s.sectionHeadLeft}>
              <TypographyInline
                size="sm"
                color={GREY.PRIMARY}
                fontWeight={fontWeight.SEMIBOLD}
                label="Sending Window"
              />
              {activeDaysCount > 0 && (
                <Box sx={s.countChip}>
                  <TypographyInline
                    size="xs"
                    color={SUCCESS.W_600}
                    fontWeight={fontWeight.SEMIBOLD}
                    label={`${activeDaysCount} day${activeDaysCount !== 1 ? "s" : ""}`}
                  />
                </Box>
              )}
            </Box>
            <Box sx={s.actions}>
              <Box sx={s.actionLink} onClick={handleSelectAll}>
                <Checkbox
                  noMargin
                  checked={allSelected}
                  onChange={() => {}}
                  style={{ pointerEvents: "none" }}
                />
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={allSelected ? "Deselect all" : "Select all"}
                />
              </Box>
              <Box sx={s.actionSep} />
              <Box sx={s.actionLink} onClick={handleReset}>
                <IconComponent
                  iconLabel={ICON_LABELS.RESTART_ALT}
                  color={GREY.TERTIARY}
                  fontSize="13px"
                />
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.MEDIUM}
                  label="Reset"
                />
              </Box>
            </Box>
          </Box>

          {/* TimeScheduler */}
          <TimeScheduler
            payload={getTimings}
            getData={(t) => setGetTimings(t)}
          />
        </Box>

        {/* ========== RIGHT: PREVIEW ========== */}
        <Box sx={s.right}>
          <Box sx={s.previewTitle}>
            <IconComponent
              iconLabel={ICON_LABELS.EVENT}
              color={BRAND.PRIMARY}
              fontSize="16px"
            />
            <TypographyInline
              label="Schedule Overview"
              size="sm"
              fontWeight={fontWeight.SEMIBOLD}
              color={GREY.PRIMARY}
            />
          </Box>

          {/* 2x2 Stat Cards */}
          <Box sx={s.statsGrid}>
            <Box sx={s.stat}>
              <Box sx={{ ...s.statIcon, background: HELP.W_50 }}>
                <IconComponent
                  iconLabel={ICON_LABELS.SCHEDULE}
                  color={HELP.W_500}
                  fontSize="15px"
                />
              </Box>
              <Box sx={s.statText}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="Starts"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label={new Date(scheduleDate).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                />
              </Box>
            </Box>

            <Box sx={s.stat}>
              <Box sx={{ ...s.statIcon, background: SUCCESS.W_50 }}>
                <IconComponent
                  iconLabel={ICON_LABELS.LANGUAGE}
                  color={SUCCESS.W_500}
                  fontSize="15px"
                />
              </Box>
              <Box sx={s.statText}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="Timezone"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label={
                    selectedTz?.split("/").pop()?.replace(/_/g, " ") ||
                    selectedTz
                  }
                />
              </Box>
            </Box>

            <Box sx={s.stat}>
              <Box sx={{ ...s.statIcon, background: WARNING.W_50 }}>
                <IconComponent
                  iconLabel={ICON_LABELS.DATE_RANGE}
                  color={WARNING.W_500}
                  fontSize="15px"
                />
              </Box>
              <Box sx={s.statText}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="Active Days"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label={`${activeDaysCount} of 7`}
                />
              </Box>
            </Box>

            <Box sx={s.stat}>
              <Box sx={{ ...s.statIcon, background: PRIMARY.W_50 }}>
                <IconComponent
                  iconLabel={ICON_LABELS.TIMER}
                  color={BRAND.PRIMARY}
                  fontSize="15px"
                />
              </Box>
              <Box sx={s.statText}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="Weekly Hours"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label={weeklyLabel}
                />
              </Box>
            </Box>
          </Box>

          {/* ===== Visual Timeline ===== */}
          <Box sx={s.timelineCard}>
            <Box sx={s.timelineHeader}>
              <TypographyInline
                label="Weekly Timeline"
                size="xs"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.SECONDARY}
              />
              {/* Time markers */}
              <Box sx={s.timeMarkers}>
                <TypographyInline
                  size="xs"
                  color={GREY.DISABLED}
                  label="6AM"
                  style={{ fontSize: "9px" }}
                />
                <TypographyInline
                  size="xs"
                  color={GREY.DISABLED}
                  label="12PM"
                  style={{ fontSize: "9px" }}
                />
                <TypographyInline
                  size="xs"
                  color={GREY.DISABLED}
                  label="6PM"
                  style={{ fontSize: "9px" }}
                />
                <TypographyInline
                  size="xs"
                  color={GREY.DISABLED}
                  label="10PM"
                  style={{ fontSize: "9px" }}
                />
              </Box>
            </Box>

            <Box sx={s.timelineRows}>
              {CAMPAIGN_CREATE_DAYS_LIST.map((d, i) => {
                const timing = getTimings.find((t) => t.day === i);
                const isActive = timing?.checked;
                const barStyle = isActive ? getBarStyle(timing) : {};

                return (
                  <Box key={i} sx={s.timelineRow}>
                    <Box sx={s.timelineDayLabel}>
                      <Box
                        sx={{
                          ...s.timelineDot,
                          background: isActive ? SUCCESS.W_500 : BORDER.DEFAULT,
                        }}
                      />
                      <TypographyInline
                        size="xs"
                        fontWeight={
                          isActive ? fontWeight.SEMIBOLD : fontWeight.REGULAR
                        }
                        color={isActive ? GREY.PRIMARY : GREY.DISABLED}
                        label={DAY_SHORT[i]}
                      />
                    </Box>

                    {/* Timeline bar track */}
                    <Box sx={s.timelineTrack}>
                      {isActive && (
                        <Box
                          sx={{
                            ...s.timelineBar,
                            left: barStyle.left,
                            width: barStyle.width,
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={s.timelineTimeLabel}>
                      <TypographyInline
                        size="xs"
                        color={isActive ? GREY.SECONDARY : GREY.DISABLED}
                        fontWeight={fontWeight.MEDIUM}
                        label={isActive ? formatTimeRange(timing) : "Off"}
                        style={{ fontSize: "10px", whiteSpace: "nowrap" }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Info */}
          {/* <Box sx={s.infoBox}>
            <IconComponent
              iconLabel={ICON_LABELS.INFO}
              color={HELP.W_500}
              fontSize="14px"
            />
            <TypographyInline
              label="Emails are sent only during active days within configured time windows. Click a day to toggle it on or off."
              size="xs"
              color={GREY.SECONDARY}
              style={{ marginLeft: "8px", lineHeight: "1.5" }}
            />
          </Box> */}
        </Box>
      </Box>

      <style>{`
        @keyframes schedulePulse {
          0% { transform: scale(0.95); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0.7; }
        }
      `}</style>
    </CustomModal>
  );
};

/* ========== STYLES ========== */
const s = {
  content: {
    display: "flex",
    minHeight: "460px",
    maxHeight: "78vh",
  },

  // -- Left --
  left: {
    flex: 1.6,
    padding: "18px 20px",
    borderRight: `1px solid ${BORDER.DEFAULT}`,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    "&::-webkit-scrollbar": { width: "3px" },
    "&::-webkit-scrollbar-thumb": {
      background: GREY.DISABLED,
      borderRadius: "3px",
    },
  },

  topRow: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-end",
  },
  field: { flex: 1.3, display: "flex", flexDirection: "column", gap: "6px" },
  fieldSm: { flex: 0.9, display: "flex", flexDirection: "column", gap: "6px" },
  fieldHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "22px",
  },

  clockBadge: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "1px 8px",
    borderRadius: "10px",
    background: "#F5F5F5",
    border: `1px solid ${BORDER.DEFAULT}`,
  },
  clockDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: SUCCESS.W_500,
    animation: "schedulePulse 2s infinite ease-in-out",
  },

  divider: {
    height: "1px",
    background: BORDER.LIGHT,
  },

  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionHeadLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  countChip: {
    padding: "1px 8px",
    borderRadius: "10px",
    background: SUCCESS.W_50,
    border: `1px solid ${SUCCESS.W_100}`,
  },

  actions: { display: "flex", alignItems: "center", gap: "2px" },
  actionLink: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "3px 7px",
    borderRadius: "6px",
    transition: "background 0.15s",
    "&:hover": { background: "#F5F5F5" },
  },
  actionSep: {
    width: "1px",
    height: "12px",
    background: BORDER.DEFAULT,
    margin: "0 2px",
  },

  // -- Right --
  right: {
    flex: 0.9,
    padding: "18px 16px",
    background: "#FAFBFC",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: "3px" },
    "&::-webkit-scrollbar-thumb": {
      background: GREY.DISABLED,
      borderRadius: "3px",
    },
  },

  previewTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  // Stats
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  stat: {
    background: BASE.WHITE,
    borderRadius: "10px",
    padding: "11px 12px",
    border: `1px solid ${BORDER.LIGHT}`,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: BORDER.DEFAULT },
  },
  statIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statText: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
  },

  // Timeline
  timelineCard: {
    background: BASE.WHITE,
    borderRadius: "10px",
    border: `1px solid ${BORDER.LIGHT}`,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  timelineHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "2px",
  },
  timeMarkers: {
    display: "flex",
    gap: "16px",
  },
  timelineRows: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  timelineRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "22px",
  },
  timelineDayLabel: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    minWidth: "42px",
    flexShrink: 0,
  },
  timelineDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  timelineTrack: {
    flex: 1,
    height: "8px",
    background: "#F0F0F0",
    borderRadius: "4px",
    position: "relative",
    overflow: "hidden",
  },
  timelineBar: {
    position: "absolute",
    top: 0,
    height: "100%",
    background: BRAND.PRIMARY,
    borderRadius: "4px",
    transition: "all 0.25s ease",
  },
  timelineTimeLabel: {
    minWidth: "108px",
    textAlign: "right",
    flexShrink: 0,
  },

  // Info
  infoBox: {
    padding: "10px 12px",
    background: HELP.W_50,
    borderRadius: "8px",
    display: "flex",
    alignItems: "flex-start",
    border: `1px solid ${HELP.W_100}`,
    marginTop: "auto",
  },

  footerRight: {
    display: "flex",
    gap: "12px",
  },
};

export default ScheduleAutomatedCampaignModal;
